/**
 * V8 raw fetch helper — for V8-owned endpoints that live OUTSIDE the frozen
 * openapi surface (/notifications/prefs, /reports/mine, GET
 * /settings/hidden-words, /admin/* thin-CRUD). Mirrors src/lib/api.ts
 * behaviors: bearer token, one refresh-and-retry on 401, error envelope.
 */
import { API_BASE, ApiError, refreshSession } from "../../lib/api.js";
import { sessionStore } from "../../lib/store.js";

export async function rawApi<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
  retried = false,
): Promise<T> {
  const token = sessionStore.get().accessToken;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (token) headers["authorization"] = `Bearer ${token}`;

  const res = await fetch(API_BASE + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "include",
  });

  if (res.status === 401 && !retried && token) {
    const ok = await refreshSession();
    if (ok) return rawApi<T>(method, path, body, true);
  }
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let envelope: ConstructorParameters<typeof ApiError>[1] = {};
    try {
      envelope = (await res.json()) as typeof envelope;
    } catch {
      envelope = { code: `HTTP_${res.status}`, message: res.statusText };
    }
    throw new ApiError(res.status, envelope);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
